import {
  useRef,
  useCallback,
  PropsWithChildren,
  isValidElement,
  cloneElement,
  MouseEvent,
} from 'react';
import { useMedia } from '../media.context';
import { MediaItem } from '../media.types';
import { Button, SVG } from '../../../components-v4';
import { Icons } from '../../icons';
import { HiddenInput } from '../media.styled';
import { MIME_MAP } from '../media.constants';
import { integerDeclination } from 'app/pages/request-single-page/utils';

type UploaderProps = {
  accept?: string;
  maxSizeMB?: number;
  maxFiles?: number;
  onSuccess?: (msg: string) => void;
  onError?: (msg: string) => void;
  asChild?: boolean;
};

export const Uploader = ({
  accept = 'image/*,video/*,.pdf,.doc,.docx',
  maxSizeMB = 10,
  maxFiles,
  onSuccess,
  onError,
  asChild,
  children,
}: PropsWithChildren<UploaderProps>) => {
  const { addItems, items } = useMedia();
  const inputRef = useRef<HTMLInputElement>(null);

  const disabled = maxFiles === 0 || maxFiles === items.length;

  const processFiles = useCallback(
    (files: FileList | null) => {
      if (!files) {
        return;
      }

      let wasError = false;
      const selectedFiles = Array.from(files);

      const availableSlots =
        maxFiles !== undefined
          ? Math.max(maxFiles - items.length, 0)
          : Infinity;

      const acceptedFiles = selectedFiles.slice(0, availableSlots);

      if (maxFiles !== undefined && selectedFiles.length > availableSlots) {
        wasError = true;
        onError?.(
          `Можно загрузить максимум ${maxFiles} ${integerDeclination(maxFiles, [
            'вложение',
            'вложения',
            'вложений',
          ])}`
        );
      }

      const errors: string[] = [];

      const newItems: MediaItem[] = acceptedFiles.flatMap((file) => {
        if (file.size > maxSizeMB * 1024 * 1024) {
          errors.push(`${file.name} превышает ${maxSizeMB}MB`);

          return [];
        }

        const src = URL.createObjectURL(file);

        const parts = file.name.split('.');

        const extension =
          parts.length > 1
            ? parts.pop()?.toLowerCase() ?? 'unknown'
            : 'unknown';

        const mimeType =
          MIME_MAP[extension as keyof typeof MIME_MAP] ?? MIME_MAP.unknown;

        const type = (mimeType.split('/')?.[0] as MediaItem['type']) || 'other';

        return [
          {
            id: `${Date.now()}-${Math.random()}`,
            src,
            name: file.name,
            alt: file.name,
            file,
            type,
            status: 'ready' as const,
            extension,
            mimeType,
          },
        ];
      });

      if (errors.length) {
        wasError = true;
        onError?.(errors.join('\n'));
      }

      if (newItems.length) {
        addItems(newItems);
      }

      if (!wasError) {
        onSuccess?.('Успешно загружено');
      }
    },
    [addItems, items.length, maxFiles, maxSizeMB, onError, onSuccess]
  );

  const handleClick = useCallback((event: MouseEvent) => {
    event.preventDefault();
    inputRef.current?.click();
  }, []);

  const trigger =
    asChild && isValidElement(children) ? (
      cloneElement(children, {
        disabled,
        onClick: (event: MouseEvent) => {
          children.props.onClick?.(event);

          if (!event.defaultPrevented) {
            handleClick(event);
          }
        },
      })
    ) : (
      <Button
        leftIcon={<SVG src={Icons.Plus} />}
        mode="neutral"
        size="s"
        disabled={disabled}
        onClick={handleClick}
      >
        Добавить файл
      </Button>
    );

  return (
    <>
      <HiddenInput
        ref={inputRef}
        type="file"
        multiple
        accept={accept}
        disabled={disabled}
        onChange={(e) => processFiles(e.target.files)}
      />
      {trigger}
    </>
  );
};
